import { useState } from "react";
// Link is hidden with the Min side links below: import { Link, useParams, useSearchParams }
import { useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Card, Descriptions, Input, Modal, Space, Spin, Typography } from "antd";
import {
  formatBankAccount,
  formatGrams,
  type AssessmentSettlementType,
  type DeliveryStatusResponse,
} from "@gull/shared";
import { ErrorAlert, PageStack } from "@gull/ui";
import { CustomerCommentCard } from "../../components/CustomerCommentCard";
import { DeliveryOfferCard } from "./DeliveryOfferCard";
import { useDeliveryActions } from "../../lib/useDeliveryActions";
import { useDeliveryStatus } from "./hooks/useDeliveryStatus";

const { Title, Paragraph, Text } = Typography;

const APPROVAL_ERROR_COPY: Record<string, string> = {
  TOKEN_EXPIRED: "Godkjenningslenken er utløpt",
  TOKEN_REVOKED: "Godkjenningslenken er ikke lenger aktiv",
  BAD_TOKEN: "Godkjenningslenken er ugyldig",
  NOT_FOUND: "Godkjenningslenken er ugyldig",
  MISMATCH: "Godkjenningslenken hører til en annen innlevering",
};

function DeliveryItemsList({ items }: { items: DeliveryStatusResponse["items"] }) {
  return (
    <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
      {items.map((item) => (
        <li key={item.id} style={{ marginBottom: 6 }}>
          <Text>{item.description ?? "Uten beskrivelse"}</Text>
        </li>
      ))}
    </ul>
  );
}

export default function DeliveryStatus() {
  const { referenceNumber, approvalToken } = useParams<{
    referenceNumber?: string;
    approvalToken?: string;
  }>();
  const [searchParams] = useSearchParams();
  // Presentation only — the approval route has its own title and layout.
  const isApprovalLink = !!approvalToken;
  // Capability. Either URL shape may carry it; whether it actually held is the server's answer,
  // read off `approval` below rather than tracked here.
  const token = approvalToken ?? searchParams.get("token");
  const {
    status: data,
    loading: fetching,
    error,
    refetch,
    setCustomerComment,
  } = useDeliveryStatus(referenceNumber ?? null, token);
  const [responseDraft, setResponseDraft] = useState<{
    response: "ACCEPTED" | "DECLINED";
    comment: string;
    settlementType: AssessmentSettlementType;
  } | null>(null);

  // The approval route has no reference in its URL, so the reference comes off the payload.
  const commentReference = data?.referenceNumber ?? referenceNumber ?? null;
  const canAct = !!data?.approval;
  const { responding, saveComment, respond } = useDeliveryActions({
    reference: commentReference,
    token,
  });
  const approvalErrorMessage = data?.approvalError ? APPROVAL_ERROR_COPY[data.approvalError] : null;

  const saveCustomerComment = async (customerComment: string | null) => {
    const result = await saveComment(customerComment);
    if (result.ok) {
      setCustomerComment(result.customerComment);
      return;
    }
    // An expired or revoked token means the actions are gone — re-render as read-only.
    await refetch();
  };

  const answerOffer = async (
    response: "ACCEPTED" | "DECLINED",
    comment: string,
    settlementType: AssessmentSettlementType,
  ) => {
    if (!canAct) return;
    const result = await respond(response, settlementType, comment);
    if (!result.ok) return;
    setResponseDraft(null);
    await refetch();
  };

  if (!referenceNumber && !token) return null;

  const displayReference = data?.referenceNumber ?? referenceNumber ?? "";

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", padding: "32px 24px 64px" }}>
      <PageStack>
        <div>
          <Title level={2} style={{ marginBottom: 8 }}>
            {isApprovalLink ? "Godkjenn innleveringen din" : "Følg innleveringen din"}
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {displayReference ? `Referanse ${displayReference.toUpperCase()}` : "Kundelenke"}
          </Paragraph>
        </div>

        {fetching ? <Spin size="large" /> : null}
        {!fetching && error ? <ErrorAlert error={error} /> : null}

        {!fetching && !error && data ? (
          <>
            {approvalErrorMessage ? (
              <Alert
                type="warning"
                showIcon
                message={approvalErrorMessage}
                description="Du kan fortsatt følge innleveringen her. Ta kontakt med oss hvis du trenger en ny lenke."
              />
            ) : null}
            <Card>
              <Space direction="vertical" size="small" style={{ display: "flex" }}>
                <Title level={3} style={{ margin: 0 }}>
                  {data.status.message}
                </Title>
                <Space wrap style={{ marginTop: 8 }}>
                  <Button onClick={() => void refetch()}>Oppdater status</Button>
                </Space>
              </Space>
            </Card>

            <Card title="Oppsummering">
              <Descriptions column={{ xs: 1, sm: 2 }} size="small">
                <Descriptions.Item label="Referanse">
                  <Text code>{data.referenceNumber}</Text>
                </Descriptions.Item>
                {data.queueNumber != null ? (
                  <Descriptions.Item label="Kønummer">#{data.queueNumber}</Descriptions.Item>
                ) : null}
                <Descriptions.Item label="Registrert">
                  {new Date(data.createdAt).toLocaleString("nb-NO", {
                    timeZone: "Europe/Oslo",
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </Descriptions.Item>
                <Descriptions.Item label="Navn">{data.customerName ?? "—"}</Descriptions.Item>
                {/* Short labels on purpose. The card is a grid of three inline label+value cells per
                    row, so a label as long as the form's "Kontonummer for utbetaling" leaves the
                    value a column of digits, and a cell spanning the row lines up with nothing. */}
                {data.payoutAccount ? (
                  <Descriptions.Item label="Kontonummer">
                    {data.payoutAccount.bankAccountNumber ? (
                      <>
                        <Text style={{ whiteSpace: "nowrap" }}>
                          {formatBankAccount(data.payoutAccount.bankAccountNumber)}
                        </Text>
                        {/* Hidden until there is a proper Min side; reintroduce the link with proper
                            styling then.
                        {data.payoutAccount.ownProfile ? (
                          <Link to="/min-side" style={{ display: "block" }}>
                            Endre på Min side
                          </Link>
                        ) : null} */}
                      </>
                    ) : (
                      <>
                        <Text>Ikke registrert</Text>
                        {data.payoutAccount.ownProfile ? null /* Hidden until there is a proper Min
                          side; reintroduce the link with proper styling then.
                          <Link to="/min-side" style={{ display: "block" }}>
                            Legg inn på Min side
                          </Link> */ : (
                          <Text type="secondary" style={{ display: "block" }}>
                            Ta kontakt med oss for å legge inn kontonummer
                          </Text>
                        )}
                      </>
                    )}
                  </Descriptions.Item>
                ) : null}
                <Descriptions.Item label="Antall gjenstander">{data.itemCount}</Descriptions.Item>
                {data.receivedWeightGrams != null ? (
                  <Descriptions.Item label="Vekt innleverte gjenstander">
                    {formatGrams(data.receivedWeightGrams)}
                  </Descriptions.Item>
                ) : null}
              </Descriptions>
              {isApprovalLink ? (
                <div style={{ marginTop: 18 }}>
                  <Text strong style={{ display: "block" }}>
                    Gjenstander ({data.items.length})
                  </Text>
                  <DeliveryItemsList items={data.items} />
                </div>
              ) : null}
            </Card>

            {canAct ? (
              <CustomerCommentCard
                comment={data.customerComment}
                placeholder="Legg til en beskjed om innleveringen"
                onSave={saveCustomerComment}
              />
            ) : null}

            <DeliveryOfferCard
              delivery={data}
              canAct={canAct}
              responding={responding}
              onRespond={(response, settlementType) =>
                setResponseDraft({ response, comment: "", settlementType })
              }
            />

            {!isApprovalLink ? (
              <Card title={`Gjenstander (${data.items.length})`}>
                <DeliveryItemsList items={data.items} />
              </Card>
            ) : null}
          </>
        ) : null}
      </PageStack>
      <Modal
        className="customer-response-modal"
        title={responseDraft?.response === "ACCEPTED" ? "Godkjenn tilbudet?" : "Avslå tilbudet?"}
        open={responseDraft !== null}
        onCancel={() => setResponseDraft(null)}
        onOk={() => {
          if (responseDraft) {
            void answerOffer(
              responseDraft.response,
              responseDraft.comment,
              responseDraft.settlementType,
            );
          }
        }}
        okText={responseDraft?.response === "ACCEPTED" ? "Godkjenn" : "Avslå"}
        okButtonProps={{ danger: responseDraft?.response === "DECLINED" }}
        confirmLoading={responding !== null}
        cancelText="Avbryt"
      >
        <Paragraph type="secondary">
          {responseDraft?.response === "ACCEPTED"
            ? responseDraft.settlementType === "GIFT_CARD"
              ? "Du velger gavekort hos David-Andersen som oppgjør for tilbudet."
              : "Du velger utbetaling til bankkonto som oppgjør for tilbudet."
            : "Vi registrerer at du ikke ønsker å selge på dette tilbudet. Du får e-post når innleveringen er klargjort for henting, og den må hentes innen 3 måneder etter at e-posten er sendt. Legg gjerne ved en kommentar."}
        </Paragraph>
        <Input.TextArea
          rows={3}
          value={responseDraft?.comment ?? ""}
          onChange={(event) =>
            setResponseDraft((current) =>
              current ? { ...current, comment: event.target.value } : current,
            )
          }
          placeholder="Kommentar (valgfritt)"
          maxLength={1000}
          showCount
        />
      </Modal>
    </div>
  );
}
